import { SpouseType } from 'apps/user/types/on-boarding.type';
import { useCreateSpouse } from 'common/queries-and-mutations/spouse';
import Button from 'components/Form/Button';
import GenderField from 'components/Form/GenderField';
import TextField from 'components/Form/TextField';
import { Icon, Icons } from 'components/Icon';
import { InfoModal } from 'components/Modal';
import { Alert } from 'components/Toast';
import { useFormik } from 'formik';
import React, { useEffect, useState } from 'react';
import { toast } from 'react-toastify';
import { services } from 'service';
import * as Yup from 'yup';

type Props = {
  onClose: () => void;
  onSuccess: () => void;
};

export default function AddSpouseModal({
  onClose,
  onSuccess
}: Readonly<Props>) {
  const [isSubmitting, setIsSubmitting] = useState(false);
  const { mutate, isSuccess, isError, isLoading } = useCreateSpouse();

  const formik = useFormik({
    initialValues: {
      spouse: [
        {
          spouseFirstName: '',
          spouseMiddleName: '',
          spouseLastName: '',
          homeAddress: '',
          emailAddress: '',
          phoneNumber: '',
          gender: '',
          isBeneficiary: false
        }
      ]
    },
    validationSchema: Yup.object({
      spouse: Yup.array().of(
        Yup.object().shape({
          spouseFirstName: Yup.string()
            .required('Spouse First Name is required')
            .nullable(),
          // spouseMiddleName: Yup.string().required(
          //   'Spouse Middle Name is required'
          // ),
          spouseLastName: Yup.string()
            .required('Spouse Last Name is required')
            .nullable(),
          homeAddress: Yup.string()
            .required('Home address is required')
            .nullable(),
          phoneNumber: Yup.string()
            .required('Phone number is required')
            .nullable(),
          gender: Yup.string().required('Gender is required').nullable(),
          emailAddress: Yup.string()
            .email('Invalid email')
            .required('Email is required')
            .nullable()
            .test('is-allowed', 'Email already in use', async function (value) {
              if (value) {
                const isInUse =
                  await services.spouseService.checkIfEmailInUse(value);
                return !isInUse;
              }
              return true;
            })
        })
      )
    }),
    onSubmit: (values, { setFieldTouched }) => {
      setIsSubmitting(true);
      for (const key in values) {
        setFieldTouched(key, true);
      }
      onSpouseSubmit(values);
    }
  });

  const onSpouseSubmit = (formValue: SpouseType) => {
    mutate(formValue);
  };

  useEffect(() => {
    if (isSuccess && !isError) {
      toast(<Alert message="Spouse Successfully Added" type="success" />);
      setIsSubmitting(false);
      onSuccess();
    }
    if (isError) {
      setIsSubmitting(false);
    }
  }, [isSuccess, isError, onSuccess]);

  return (
    <InfoModal
      width="w-[55%]"
      className="h-fit rounded-md px-4 pb-9 pt-5 sm:p-9"
    >
      <div>
        <div className="flex flex-row items-center justify-between pl-6">
          <p className="text-2xl font-bold text-[#353535]">Add Spouse</p>
          <button type="button" onClick={onClose}>
            <Icon name={Icons.Close} />
          </button>
        </div>
        <form
          autoComplete="false"
          className="mt-10 px-6"
          onSubmit={formik.handleSubmit}
        >
          {formik.values.spouse.map((spouse, index) => (
            <div key={index} className="my-8s">
              <div className="grid md:grid-cols-2 md:gap-6">
                <div className="group relative z-0 mb-6 w-full">
                  <TextField
                    name={`spouse[${index}].spouseFirstName`}
                    placeholder=""
                    value={spouse.spouseFirstName}
                    onChange={formik.handleChange}
                    onBlur={formik.handleBlur}
                    label="Spouse First Name"
                    error={
                      formik.touched.spouse
                        ? formik.errors.spouse?.[index] &&
                          formik.errors.spouse.length > 0
                          ? JSON.parse(
                              JSON.stringify(formik.errors.spouse[index])
                            ).spouseFirstName
                          : ''
                        : ''
                    }
                    size="lg"
                    className="rounded-2xl border-[1.5px] border-gray-300"
                    isRequired
                  />
                </div>
                <div className="group relative z-0 mb-6 w-full">
                  <TextField
                    name={`spouse[${index}].spouseMiddleName`}
                    placeholder=""
                    value={spouse.spouseMiddleName}
                    onChange={formik.handleChange}
                    onBlur={formik.handleBlur}
                    label="Spouse Middle Name"
                    error={
                      formik.touched.spouse
                        ? formik.errors.spouse?.[index] &&
                          formik.errors.spouse.length > 0
                          ? JSON.parse(
                              JSON.stringify(formik.errors.spouse[index])
                            ).spouseMiddleName
                          : ''
                        : ''
                    }
                    size="lg"
                    className="rounded-2xl border-[1.5px] border-gray-300"
                  />
                </div>
              </div>
              <div className="grid md:grid-cols-2 md:gap-6">
                <div className="group relative z-0 mb-6 w-full">
                  <TextField
                    name={`spouse[${index}].spouseLastName`}
                    placeholder=""
                    value={spouse.spouseLastName}
                    onChange={formik.handleChange}
                    onBlur={formik.handleBlur}
                    label="Spouse Last Name"
                    error={
                      formik.touched.spouse
                        ? formik.errors.spouse?.[index] &&
                          formik.errors.spouse.length > 0
                          ? JSON.parse(
                              JSON.stringify(formik.errors.spouse[index])
                            ).spouseLastName
                          : ''
                        : ''
                    }
                    size="lg"
                    className="rounded-2xl border-[1.5px] border-gray-300"
                    isRequired
                  />
                </div>
                <div className="group relative z-0 mb-6 w-full">
                  <TextField
                    name={`spouse[${index}].homeAddress`}
                    placeholder=""
                    value={spouse.homeAddress}
                    onChange={formik.handleChange}
                    onBlur={formik.handleBlur}
                    label="Home Address"
                    error={
                      formik.touched.spouse
                        ? formik.errors.spouse?.[index] &&
                          formik.errors.spouse.length > 0
                          ? JSON.parse(
                              JSON.stringify(formik.errors.spouse[index])
                            ).homeAddress
                          : ''
                        : ''
                    }
                    size="lg"
                    className="rounded-2xl border-[1.5px] border-gray-300"
                    isRequired
                  />
                </div>
              </div>
              <div className="grid md:grid-cols-2 md:gap-6">
                <div className="group relative z-0 mb-6 w-full">
                  <TextField
                    name={`spouse[${index}].emailAddress`}
                    placeholder=""
                    value={spouse.emailAddress}
                    onChange={formik.handleChange}
                    onBlur={formik.handleBlur}
                    label="Email Address"
                    error={
                      formik.touched.spouse
                        ? formik.errors.spouse?.[index] &&
                          formik.errors.spouse.length > 0
                          ? JSON.parse(
                              JSON.stringify(formik.errors.spouse[index])
                            ).emailAddress
                          : ''
                        : ''
                    }
                    size="lg"
                    className="rounded-2xl border-[1.5px] border-gray-300"
                    isRequired
                  />
                </div>
                <div className="group relative z-0 mb-6 w-full">
                  <TextField
                    name={`spouse[${index}].phoneNumber`}
                    placeholder=""
                    value={spouse.phoneNumber}
                    onChange={formik.handleChange}
                    onBlur={formik.handleBlur}
                    label="Phone Number"
                    error={
                      formik.touched.spouse
                        ? formik.errors.spouse?.[index] &&
                          formik.errors.spouse.length > 0
                          ? JSON.parse(
                              JSON.stringify(formik.errors.spouse[index])
                            ).phoneNumber
                          : ''
                        : ''
                    }
                    size="lg"
                    className="rounded-2xl border-[1.5px] border-gray-300"
                    isRequired
                  />
                </div>
              </div>
              <div className="grid md:grid-cols-2 md:gap-6">
                <div className="group relative z-0 mb-6 w-full">
                  <GenderField
                    gender={spouse.gender}
                    onSelect={gender =>
                      formik.setFieldValue(`spouse[${index}].gender`, gender)
                    }
                    isRequired
                  />
                </div>
              </div>
            </div>
          ))}

          <div className=" flex items-center justify-between gap-2">
            <div className="flex items-center gap-2">
              <Icon name={Icons.Required} />
              <span className="text-[10px] text-[#7A7C87]">
                Indicates Required
              </span>
            </div>
          </div>

          <div className="flex w-full items-end justify-end gap-4">
            <div className="mb-4 flex items-center">
              <label
                htmlFor="isBeneficiary"
                className="mr-2 text-sm font-medium text-gray-900"
              >
                <input
                  id="isBeneficiary"
                  type="checkbox"
                  name={`spouse[${0}].isBeneficiary`}
                  onChange={() =>
                    formik.setFieldValue(
                      `spouse[${0}].isBeneficiary`,
                      !formik.values.spouse[0].isBeneficiary
                    )
                  }
                  defaultChecked={formik.values.spouse[0].isBeneficiary}
                  className="mx-2 h-4 w-4 rounded border-gray-300 bg-white text-[#FF6600] focus:ring-2 focus:ring-[#FF6600]"
                />
                Save as beneficiary
              </label>
            </div>

            <Button
              variant="primary"
              size="custom"
              className="mt-5 w-[9rem] rounded p-2.5"
              label="Add Spouse"
              type="submit"
              disabled={!formik.isValid}
              loading={isSubmitting || isLoading}
            ></Button>
          </div>
        </form>
      </div>
    </InfoModal>
  );
}
